import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PosterCard from "../../components/PosterCard/index.tsx";
import {
  useGetDiscoverMediaQuery,
  useGetRecommendationsQuery,
} from "../../store/api/genreApi.ts";
import { FetchAPIData } from "../../utils/GlobalFunctions/fetch.ts";
import { RootState } from "../../store/store.ts";
import {
  MediaData,
  sessionDataType,
} from "../../utils/types/app.types.ts";
import {
  applyBrowseDisplaySort,
  BROWSE_SORT_OPTIONS,
  BrowseResultSortMode,
  filterByMinVotes,
  MIN_VOTES_OPTIONS,
  parseMinVotesParam,
} from "../../utils/browseVoteUtils.ts";

const MOVIE_SORTS = [
  { value: "popularity.desc", label: "Popularity" },
  { value: "vote_average.desc", label: "Rating" },
  { value: "vote_count.desc", label: "Most votes" },
  { value: "vote_count.asc", label: "Fewest votes" },
  { value: "primary_release_date.desc", label: "Newest release" },
  { value: "primary_release_date.asc", label: "Oldest release" },
] as const;

const TV_SORTS = [
  { value: "popularity.desc", label: "Popularity" },
  { value: "vote_average.desc", label: "Rating" },
  { value: "vote_count.desc", label: "Most votes" },
  { value: "vote_count.asc", label: "Fewest votes" },
  { value: "first_air_date.desc", label: "Newest first air" },
  { value: "first_air_date.asc", label: "Oldest first air" },
] as const;

const movieSortValues = new Set(MOVIE_SORTS.map((s) => s.value));
const tvSortValues = new Set(TV_SORTS.map((s) => s.value));

type GenreOpt = { id: number; name: string };

type SearchHit = {
  id: number;
  poster_path: string | null;
  title?: string;
  name?: string;
  release_date?: string;
  first_air_date?: string;
};

function resolveSort(
  sortParam: string,
  mediaType: "movie" | "tv",
): string {
  if (mediaType === "movie" && movieSortValues.has(sortParam as any)) {
    return sortParam;
  }
  if (mediaType === "tv" && tvSortValues.has(sortParam as any)) {
    return sortParam;
  }
  return "popularity.desc";
}

const DiscoverPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionData = useSelector((state: RootState) => state.session);

  const tab: "discover" | "recommendations" =
    searchParams.get("tab") === "recommendations"
      ? "recommendations"
      : "discover";
  const mediaType: "movie" | "tv" =
    searchParams.get("type") === "tv" ? "tv" : "movie";
  const recIdRaw = searchParams.get("id") || "";
  const sortBy = resolveSort(searchParams.get("sort") || "", mediaType);
  const genreParam = searchParams.get("genre") || "";
  const hideUnreleased = searchParams.get("hideUnreleased") === "1";
  const minVotesRaw = searchParams.get("minVotes") || "";
  const voteCountGte = parseMinVotesParam(minVotesRaw);

  const withGenres = genreParam.trim() || undefined;

  const [genres, setGenres] = useState<GenreOpt[]>([]);

  useEffect(() => {
    const { apiKey, apiUrl } = sessionData.api;
    void fetch(
      `${apiUrl}genre/${mediaType}/list?api_key=${apiKey}&language=en-US`,
    )
      .then((r) => r.json())
      .then((d: { genres?: GenreOpt[] }) => setGenres(d.genres || []))
      .catch(() => setGenres([]));
  }, [mediaType, sessionData.api]);

  const [currentPage, setCurrentPage] = useState(1);
  const [allItems, setAllItems] = useState<MediaData[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading, error, refetch } = useGetDiscoverMediaQuery(
    {
      mediaType,
      page: currentPage,
      sortBy,
      withGenres,
      hideUnreleased: mediaType === "movie" && hideUnreleased,
      voteCountGte: voteCountGte > 0 ? voteCountGte : undefined,
    },
    { skip: tab !== "discover" },
  );

  useEffect(() => {
    setCurrentPage(1);
    setAllItems([]);
    setHasMore(true);
  }, [tab, mediaType, sortBy, genreParam, hideUnreleased, minVotesRaw]);

  useEffect(() => {
    if (tab !== "discover" || !data?.items) return;
    if (
      typeof data.currentPage === "number" &&
      data.currentPage !== currentPage
    ) {
      return;
    }

    const batch = [...data.items];
    if (sortBy === "popularity.desc") {
      batch.sort((a: MediaData, b: MediaData) => {
        const dateA = new Date(
          a.release_date || a.first_air_date || 0,
        ).getTime();
        const dateB = new Date(
          b.release_date || b.first_air_date || 0,
        ).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return (b.vote_average || 0) - (a.vote_average || 0);
      });
    }

    if (currentPage === 1) {
      setAllItems(batch);
    } else {
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = batch.filter(
          (item: MediaData) => !existingIds.has(item.id),
        );
        return [...prev, ...newItems];
      });
    }
    setHasMore(currentPage < (data.totalPages || 0));
  }, [data, currentPage, tab, sortBy]);

  const observer = useRef<IntersectionObserver>();
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (tab !== "discover" || isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((p) => p + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, tab],
  );

  const patchParams = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === "") {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const setTab = (next: "discover" | "recommendations") => {
    patchParams({ tab: next === "discover" ? undefined : "recommendations" });
  };

  const setMediaType = (nextMedia: "movie" | "tv") => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("type", nextMedia);
    nextParams.delete("genre");
    if (nextMedia === "tv") {
      const s = nextParams.get("sort") || "";
      if (movieSortValues.has(s as any) && !tvSortValues.has(s as any)) {
        nextParams.delete("sort");
      }
    } else {
      const s = nextParams.get("sort") || "";
      if (tvSortValues.has(s as any) && !movieSortValues.has(s as any)) {
        nextParams.delete("sort");
      }
    }
    setSearchParams(nextParams, { replace: true });
  };

  const recIdValid = /^\d+$/.test(recIdRaw);
  const recSkip = tab !== "recommendations" || !recIdValid;
  const {
    data: recList,
    isLoading: recLoading,
    error: recError,
    refetch: recRefetch,
  } = useGetRecommendationsQuery(
    { mediaType, id: recIdRaw },
    { skip: recSkip },
  );

  const [seedTitle, setSeedTitle] = useState("");
  useEffect(() => {
    if (recSkip || !recIdRaw) {
      setSeedTitle("");
      return;
    }
    const ep = mediaType === "movie" ? `movie/${recIdRaw}` : `tv/${recIdRaw}`;
    void FetchAPIData(ep, sessionData as sessionDataType)
      .then((d: { title?: string; name?: string }) => {
        setSeedTitle(d.title || d.name || "");
      })
      .catch(() => setSeedTitle(""));
  }, [recIdRaw, mediaType, recSkip, sessionData]);

  const [seedQuery, setSeedQuery] = useState("");
  const [seedResults, setSeedResults] = useState<SearchHit[]>([]);
  const [seedSearching, setSeedSearching] = useState(false);

  const [recBrowseSort, setRecBrowseSort] =
    useState<BrowseResultSortMode>("relevance");
  const [recMinVotes, setRecMinVotes] = useState("");

  const recMinNum = parseMinVotesParam(recMinVotes);
  const displayedRecs = useMemo((): MediaData[] => {
    if (!recList?.length) return [];
    const list = recList as MediaData[];
    return applyBrowseDisplaySort(
      filterByMinVotes(list, recMinNum),
      recBrowseSort,
    );
  }, [recList, recMinNum, recBrowseSort]);

  useEffect(() => {
    setRecBrowseSort("relevance");
    setRecMinVotes("");
  }, [recIdRaw, mediaType]);

  const runSeedSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = seedQuery.trim();
    if (!q) {
      setSeedResults([]);
      return;
    }
    const { apiKey, apiUrl } = sessionData.api;
    const path =
      mediaType === "tv"
        ? `search/tv?query=${encodeURIComponent(q)}&page=1`
        : `search/movie?query=${encodeURIComponent(q)}&page=1`;
    setSeedSearching(true);
    void fetch(`${apiUrl}${path}&api_key=${apiKey}&language=en-US`)
      .then((r) => r.json())
      .then((d: { results?: SearchHit[] }) => {
        setSeedResults(d.results || []);
      })
      .catch(() => setSeedResults([]))
      .finally(() => setSeedSearching(false));
  };

  const pickSeed = (hit: SearchHit) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "recommendations");
    next.set("type", mediaType);
    next.set("id", String(hit.id));
    setSearchParams(next, { replace: true });
    setSeedResults([]);
    setSeedQuery("");
  };

  const clearSeed = () => {
    patchParams({ id: undefined });
  };

  const sortOptions = mediaType === "movie" ? MOVIE_SORTS : TV_SORTS;

  return (
    <section className="container page-section discover-page">
      <h1 className="page-section-title">Discover</h1>

      <div className="discover-tabs" role="tablist" aria-label="Discover sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "discover"}
          className={tab === "discover" ? "discover-tab is-active" : "discover-tab"}
          onClick={() => setTab("discover")}
        >
          Discover
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "recommendations"}
          className={
            tab === "recommendations" ? "discover-tab is-active" : "discover-tab"
          }
          onClick={() => setTab("recommendations")}
        >
          Recommendations
        </button>
      </div>

      {tab === "discover" && (
        <>
          <div className="discover-toolbar">
            <div className="discover-field">
              <span className="discover-label">Media</span>
              <div className="discover-segment">
                <button
                  type="button"
                  className={mediaType === "movie" ? "is-active" : ""}
                  onClick={() => setMediaType("movie")}
                >
                  Movies
                </button>
                <button
                  type="button"
                  className={mediaType === "tv" ? "is-active" : ""}
                  onClick={() => setMediaType("tv")}
                >
                  TV
                </button>
              </div>
            </div>

            <label className="discover-field">
              <span className="discover-label">Genre</span>
              <select
                value={genreParam}
                onChange={(e) =>
                  patchParams({ genre: e.target.value || undefined })
                }
              >
                <option value="">All genres</option>
                {genres.map((g) => (
                  <option key={g.id} value={String(g.id)}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="discover-field">
              <span className="discover-label">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => patchParams({ sort: e.target.value })}
              >
                {sortOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="discover-field">
              <span className="discover-label">Min. votes</span>
              <select
                value={minVotesRaw}
                onChange={(e) =>
                  patchParams({ minVotes: e.target.value || undefined })
                }
              >
                {MIN_VOTES_OPTIONS.map((o) => (
                  <option key={o.value || "none"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            {mediaType === "movie" && (
              <label className="discover-field discover-checkbox">
                <input
                  type="checkbox"
                  checked={hideUnreleased}
                  onChange={(e) =>
                    patchParams({
                      hideUnreleased: e.target.checked ? "1" : undefined,
                    })
                  }
                />
                <span>Hide unreleased</span>
              </label>
            )}
          </div>

          {isLoading && currentPage === 1 && (
            <p className="discover-status">Loading…</p>
          )}
          {error && (
            <div className="discover-status discover-error">
              <p>Something went wrong loading discover results.</p>
              <button type="button" className="btn" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          )}
          {!isLoading && !error && allItems.length === 0 && (
            <p className="discover-status">No titles match these filters.</p>
          )}
          <div className="movie-grid">
            {allItems.map((item, index: number) => (
              <PosterCard
                key={item.id}
                linkTo={
                  mediaType === "movie"
                    ? `/movie-details/${item.id}`
                    : `/tv-details/${item.id}`
                }
                title={item.title || item.name || "Untitled"}
                posterPath={item.poster_path}
                subtitle={
                  mediaType === "movie"
                    ? item.release_date
                      ? `Release: ${item.release_date}`
                      : undefined
                    : item.first_air_date
                      ? `First air: ${item.first_air_date}`
                      : undefined
                }
                rowPosition={
                  index % 4 === 0
                    ? "first"
                    : index % 4 === 3
                      ? "last"
                      : undefined
                }
              />
            ))}
          </div>
          {tab === "discover" && allItems.length > 0 && (
            <div
              ref={lastItemRef}
              className="discover-sentinel"
              aria-hidden
            />
          )}
          {isLoading && currentPage > 1 && (
            <p className="discover-status">Loading more…</p>
          )}
          {data && allItems.length > 0 && (
            <p className="discover-footer-meta">
              Showing {allItems.length}
              {typeof data.totalResults === "number"
                ? ` of ${data.totalResults} results`
                : ""}
              {hasMore ? " · Scroll for more" : ""}
            </p>
          )}
        </>
      )}

      {tab === "recommendations" && (
        <>
          <div className="discover-toolbar">
            <div className="discover-field">
              <span className="discover-label">Seed type</span>
              <div className="discover-segment">
                <button
                  type="button"
                  className={mediaType === "movie" ? "is-active" : ""}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("type", "movie");
                    next.delete("id");
                    setSearchParams(next, { replace: true });
                  }}
                >
                  Movies
                </button>
                <button
                  type="button"
                  className={mediaType === "tv" ? "is-active" : ""}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("type", "tv");
                    next.delete("id");
                    setSearchParams(next, { replace: true });
                  }}
                >
                  TV
                </button>
              </div>
            </div>
          </div>

          {!recIdValid && (
            <div className="discover-seed-panel">
              <p>
                Search for a {mediaType === "movie" ? "movie" : "TV show"} to
                use as a starting point. Results are powered by TMDB’s
                recommendations for that title.
              </p>
              <form className="discover-seed-form" onSubmit={runSeedSearch}>
                <input
                  type="search"
                  value={seedQuery}
                  onChange={(e) => setSeedQuery(e.target.value)}
                  placeholder={`Search ${mediaType === "movie" ? "movies" : "TV shows"}…`}
                  aria-label="Search for a title"
                />
                <button type="submit" className="btn" disabled={seedSearching}>
                  {seedSearching ? "Searching…" : "Search"}
                </button>
              </form>
              {seedResults.length > 0 && (
                <ul className="discover-seed-results">
                  {seedResults.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        className="discover-seed-pick"
                        onClick={() => pickSeed(hit)}
                      >
                        <span className="discover-seed-title">
                          {hit.title || hit.name}
                        </span>
                        <span className="discover-seed-meta">
                          {hit.release_date || hit.first_air_date || "—"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {recIdValid && (
            <>
              <div className="discover-rec-header">
                <div>
                  <h2 className="page-section-title discover-rec-heading">
                    {seedTitle || `Title #${recIdRaw}`}
                  </h2>
                  <p className="discover-rec-sub">
                    Because you viewed this {mediaType === "movie" ? "movie" : "show"}
                    …
                  </p>
                </div>
                <div className="discover-rec-actions">
                  <button type="button" className="btn" onClick={clearSeed}>
                    Change title
                  </button>
                  <Link
                    className="btn"
                    to={
                      mediaType === "movie"
                        ? `/movie-details/${recIdRaw}`
                        : `/tv-details/${recIdRaw}`
                    }
                  >
                    Open details
                  </Link>
                </div>
              </div>

              <div className="discover-toolbar discover-rec-toolbar">
                <label className="discover-field">
                  <span className="discover-label">Sort</span>
                  <select
                    value={recBrowseSort}
                    onChange={(e) =>
                      setRecBrowseSort(e.target.value as BrowseResultSortMode)
                    }
                  >
                    {BROWSE_SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="discover-field">
                  <span className="discover-label">Min. votes</span>
                  <select
                    value={recMinVotes}
                    onChange={(e) => setRecMinVotes(e.target.value)}
                  >
                    {MIN_VOTES_OPTIONS.map((o) => (
                      <option key={o.value || "none"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="discover-rec-scope text-muted">
                Sort and minimum votes apply to this recommendation set.
              </p>

              {recLoading && <p className="discover-status">Loading…</p>}
              {recError && (
                <div className="discover-status discover-error">
                  <p>Could not load recommendations.</p>
                  <button type="button" className="btn" onClick={() => recRefetch()}>
                    Retry
                  </button>
                </div>
              )}
              {!recLoading &&
                !recError &&
                (!recList || recList.length === 0) && (
                <p className="discover-status">No recommendations for this title.</p>
              )}
              {!recLoading &&
                !recError &&
                recList &&
                recList.length > 0 &&
                displayedRecs.length === 0 && (
                  <p className="discover-status">
                    No titles match the minimum vote filter.
                  </p>
                )}
              <div className="movie-grid">
                {displayedRecs.map((item: MediaData, index: number) => (
                  <PosterCard
                    key={item.id}
                    linkTo={
                      mediaType === "movie"
                        ? `/movie-details/${item.id}`
                        : `/tv-details/${item.id}`
                    }
                    title={item.title || item.name || "Untitled"}
                    posterPath={item.poster_path}
                    subtitle={
                      mediaType === "movie"
                        ? item.release_date
                          ? `Release: ${item.release_date}`
                          : undefined
                        : item.first_air_date
                          ? `First air: ${item.first_air_date}`
                          : undefined
                    }
                    rowPosition={
                      index % 4 === 0
                        ? "first"
                        : index % 4 === 3
                          ? "last"
                          : undefined
                    }
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default DiscoverPage;
